/**
 * ActorManagementPage - Role assignment + scope preview for actors (DRR-103, DRR-134/135)
 *
 * Lists actors filtered by ActorType. Click a row to open a detail drawer
 * where an admin can assign roles and preview the resolved scopes.
 *
 * Accepts optional `actorType` prop to filter by type (default: HUMAN).
 * Used by WorkforcePage to render setting-bundle-driven tabs.
 *
 * Uses generated hooks: useListActorsQuery, useListRolesQuery, useUpdateActorMutation.
 * Scope preview via resolveRoleScopes raw query (same pattern as DevImpersonationPanel).
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Stack,
  Group,
  Alert,
  Text,
  Badge,
  Skeleton,
  Paper,
  TextInput,
  Select,
  Table,
  Drawer,
  Button,
  MultiSelect,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconUsers,
  IconSearch,
  IconDeviceFloppy,
  IconShieldCheck,
  IconCertificate,
  IconNetwork,
} from '@tabler/icons-react';

import { useTenantId } from '@/auth';
import {
  GqlActorType,
  GqlRoleCategory,
  ResolveRoleScopesDocument,
  useConstellationsByActorQuery,
  useListActorsQuery,
  useListRolesQuery,
  useResolveRoleScopesQuery,
  useUpdateActorMutation,
} from '@/generated/graphql';
import { apolloClient } from '@/utils/apollo-client';

// ============================================================================
// Scope resolution query (raw gql — not in codegen documents)
// ============================================================================

async function fetchRoleScopes(roleIds: string[]): Promise<string[]> {
  if (roleIds.length === 0) return [];
  try {
    const { data } = await apolloClient.query<{ resolveRoleScopes: string[] }>({
      query: ResolveRoleScopesDocument,
      variables: { roleIds },
      fetchPolicy: 'network-only',
    });
    return data?.resolveRoleScopes ?? [];
  } catch (err) {
    console.warn('[ActorManagement] Failed to resolve role scopes:', err);
    return [];
  }
}

// ============================================================================
// Data-driven category display (derived from backend GqlRoleCategory enum)
// ============================================================================

const CATEGORY_COLOR_MAP: Record<string, string> = {
  [GqlRoleCategory.CLINICAL]: 'teal',
  [GqlRoleCategory.ADMINISTRATIVE]: 'blue',
  [GqlRoleCategory.EXECUTIVE]: 'red',
  [GqlRoleCategory.EXTERNAL]: 'orange',
  [GqlRoleCategory.SYSTEM]: 'grape',
  [GqlRoleCategory.TECHNICAL]: 'cyan',
};

function getCategoryColor(category: string): string {
  return CATEGORY_COLOR_MAP[category.toUpperCase()] ?? 'gray';
}

const CATEGORY_OPTIONS = Object.values(GqlRoleCategory).map((val) => ({
  value: val,
  label: val.charAt(0) + val.slice(1).toLowerCase(),
}));

// ============================================================================
// Types
// ============================================================================

type ActorRow = {
  id: string;
  name: string;
  category?: string | null;
  description?: string | null;
  roleIds: string[];
  resolvedScopes: string[];
  active: boolean;
  primaryRoleId?: string | null;
  teamId?: string | null;
  supervisorId?: string | null;
  certifications?: string[];
  specializations?: string[];
  expertTier?: string | null;
  licenseNumber?: string | null;
  licenseJurisdiction?: string | null;
};

interface ActorManagementPageProps {
  /** Filter actors by type. Defaults to HUMAN (staff). */
  actorType?: GqlActorType;
  /** Display label for the actor type (from setting bundle). */
  typeLabel?: string;
}

// ============================================================================
// Component
// ============================================================================

export function ActorManagementPage({
  actorType = GqlActorType.HUMAN,
  typeLabel,
}: ActorManagementPageProps = {}) {
  const displayLabel = typeLabel ?? actorType.charAt(0) + actorType.slice(1).toLowerCase();
  const tenantId = useTenantId();

  // Data queries
  const {
    data: actorsData,
    loading: actorsLoading,
    error: actorsError,
    refetch: refetchActors,
  } = useListActorsQuery({ variables: { limit: 200 } });

  const { data: rolesData, loading: rolesLoading } = useListRolesQuery({
    variables: { limit: 100 },
  });

  const [updateActor, { loading: saving }] = useUpdateActorMutation();

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Drawer state
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [selectedActor, setSelectedActor] = useState<ActorRow | null>(null);

  // Edit state for the drawer
  const [editRoleIds, setEditRoleIds] = useState<string[]>([]);
  const [previewScopes, setPreviewScopes] = useState<string[]>([]);
  const [scopesLoading, setScopesLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // DRR-126: Constellation memberships for selected actor (lazy via skip)
  const { data: constellationData } = useConstellationsByActorQuery({
    variables: { actorId: selectedActor?.id ?? '' },
    skip: !selectedActor,
    fetchPolicy: 'cache-first',
  });
  const constellationMemberships = constellationData?.constellationsByActor?.memberships ?? [];

  // Filter actors by the requested type
  const typeFilteredActors: ActorRow[] = useMemo(() => {
    const items = actorsData?.listActors?.items ?? [];
    return items.filter((a) => a.actorType === actorType);
  }, [actorsData, actorType]);

  // Apply search + category filter
  const filteredActors = useMemo(() => {
    let result = typeFilteredActors;
    if (categoryFilter) {
      result = result.filter(
        (a) => a.category?.toUpperCase() === categoryFilter,
      );
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.description?.toLowerCase().includes(q) ?? false),
      );
    }
    return result;
  }, [typeFilteredActors, categoryFilter, search]);

  // Role options for MultiSelect
  const roleOptions = useMemo(() => {
    const items = rolesData?.listRoles?.items ?? [];
    return items
      .filter((r) => r.active)
      .map((r) => ({
        value: r.id,
        label: `${r.roleName} (${r.roleCategory})`,
      }));
  }, [rolesData]);

  // Role name lookup
  const roleNameMap = useMemo(() => {
    const items = rolesData?.listRoles?.items ?? [];
    const map = new Map<string, string>();
    for (const r of items) {
      map.set(r.id, r.roleName);
    }
    return map;
  }, [rolesData]);

  // ========================================================================
  // Handlers
  // ========================================================================

  const handleRowClick = useCallback(
    (actor: ActorRow) => {
      setSelectedActor(actor);
      setEditRoleIds(actor.roleIds);
      setPreviewScopes(actor.resolvedScopes);
      setSaveError(null);
      openDrawer();
    },
    [openDrawer],
  );

  const handleRoleChange = useCallback(async (newRoleIds: string[]) => {
    setEditRoleIds(newRoleIds);
    // Live scope preview
    setScopesLoading(true);
    const scopes = await fetchRoleScopes(newRoleIds);
    setPreviewScopes(scopes);
    setScopesLoading(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedActor) return;
    setSaveError(null);
    try {
      await updateActor({
        variables: {
          actorId: selectedActor.id,
          input: { roleIds: editRoleIds },
        },
      });
      await refetchActors();
      closeDrawer();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
  }, [selectedActor, editRoleIds, updateActor, refetchActors, closeDrawer]);

  // Dirty check
  const isDirty = useMemo(() => {
    if (!selectedActor) return false;
    const original = [...selectedActor.roleIds].sort();
    const current = [...editRoleIds].sort();
    return JSON.stringify(original) !== JSON.stringify(current);
  }, [selectedActor, editRoleIds]);

  // ========================================================================
  // Stat cards
  // ========================================================================

  const stats = useMemo(() => {
    const total = typeFilteredActors.length;
    const active = typeFilteredActors.filter((a) => a.active).length;
    const categories = new Map<string, number>();
    for (const a of typeFilteredActors) {
      const cat = a.category ?? 'uncategorized';
      categories.set(cat, (categories.get(cat) ?? 0) + 1);
    }
    return { total, active, categories };
  }, [typeFilteredActors]);

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <Container size="xl">
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Title order={2}>{displayLabel} Management</Title>
          <Text c="dimmed" size="sm">
            View {displayLabel.toLowerCase()}s, assign roles, and preview scope access
          </Text>
        </div>

        {/* Error */}
        {actorsError && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" variant="light">
            Failed to load actors. Please try again.
          </Alert>
        )}

        {/* Stat cards */}
        <Group gap="md">
          <Paper p="sm" withBorder radius="md" style={{ flex: 1 }}>
            <Text size="xs" c="dimmed">Total {displayLabel}s</Text>
            <Text size="xl" fw={700}>{stats.total}</Text>
          </Paper>
          <Paper p="sm" withBorder radius="md" style={{ flex: 1 }}>
            <Text size="xs" c="dimmed">Active</Text>
            <Text size="xl" fw={700} c="green">{stats.active}</Text>
          </Paper>
          {Array.from(stats.categories.entries())
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([cat, count]) => (
              <Paper key={cat} p="sm" withBorder radius="md" style={{ flex: 1 }}>
                <Text size="xs" c="dimmed" tt="capitalize">{cat}</Text>
                <Text size="xl" fw={700}>{count}</Text>
              </Paper>
            ))}
        </Group>

        {/* Filters */}
        <Group gap="sm">
          <TextInput
            placeholder="Search actors..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="All categories"
            data={CATEGORY_OPTIONS}
            value={categoryFilter}
            onChange={setCategoryFilter}
            clearable
            w={180}
          />
        </Group>

        {/* Table */}
        {actorsLoading || rolesLoading ? (
          <Stack gap="xs">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={40} radius="sm" />
            ))}
          </Stack>
        ) : filteredActors.length === 0 ? (
          <Paper p="xl" withBorder ta="center">
            <Stack gap="sm" align="center">
              <IconUsers size={48} color="gray" />
              <Text size="lg" fw={500}>
                {typeFilteredActors.length === 0 ? `No ${displayLabel}s Found` : `No Matching ${displayLabel}s`}
              </Text>
              <Text c="dimmed" size="sm">
                {typeFilteredActors.length === 0
                  ? `${displayLabel}s will appear here once seeded.`
                  : 'Try adjusting your search or filter.'}
              </Text>
            </Stack>
          </Paper>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Roles</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredActors.map((actor) => (
                <Table.Tr
                  key={actor.id}
                  onClick={() => handleRowClick(actor)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>
                    <Text size="sm" fw={500}>{actor.name}</Text>
                    {actor.description && (
                      <Text size="xs" c="dimmed" lineClamp={1}>{actor.description}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {actor.category ? (
                      <Badge
                        color={getCategoryColor(actor.category)}
                        variant="light"
                        size="sm"
                      >
                        {actor.category.charAt(0) + actor.category.slice(1).toLowerCase()}
                      </Badge>
                    ) : (
                      <Text size="xs" c="dimmed">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="wrap">
                      {actor.roleIds.length === 0 ? (
                        <Text size="xs" c="dimmed">No roles</Text>
                      ) : (
                        actor.roleIds.slice(0, 3).map((rid) => (
                          <Badge key={rid} variant="outline" size="xs">
                            {roleNameMap.get(rid) ?? rid}
                          </Badge>
                        ))
                      )}
                      {actor.roleIds.length > 3 && (
                        <Badge variant="outline" size="xs" color="gray">
                          +{actor.roleIds.length - 3}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={actor.active ? 'green' : 'red'}
                      variant="light"
                      size="xs"
                    >
                      {actor.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      {/* ================================================================ */}
      {/* Actor Detail Drawer                                              */}
      {/* ================================================================ */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={selectedActor?.name ?? 'Actor Details'}
        position="right"
        size="md"
      >
        {selectedActor && (
          <Stack gap="md">
            {/* Info */}
            <div>
              <Text size="sm" c="dimmed">Category</Text>
              <Text size="sm" tt="capitalize">{selectedActor.category ?? 'Uncategorized'}</Text>
            </div>
            {selectedActor.description && (
              <div>
                <Text size="sm" c="dimmed">Description</Text>
                <Text size="sm">{selectedActor.description}</Text>
              </div>
            )}

            {/* Team & Supervisor */}
            {selectedActor.teamId && (
              <div>
                <Text size="sm" c="dimmed">Team</Text>
                <Badge variant="light" size="sm" color="blue">{selectedActor.teamId}</Badge>
              </div>
            )}
            {selectedActor.supervisorId && (
              <div>
                <Text size="sm" c="dimmed">Supervisor</Text>
                <Badge variant="light" size="sm" color="violet">{selectedActor.supervisorId}</Badge>
              </div>
            )}

            {/* Certifications (DRR-134 Phase 2) */}
            {selectedActor.certifications && selectedActor.certifications.length > 0 && (
              <div>
                <Group gap="xs" mb="xs">
                  <IconCertificate size={16} />
                  <Text size="sm" fw={600}>Certifications</Text>
                </Group>
                <Group gap={4} wrap="wrap">
                  {selectedActor.certifications.map((cert) => (
                    <Badge key={cert} variant="light" size="sm" color="green">
                      {cert}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}

            {/* Specializations */}
            {selectedActor.specializations && selectedActor.specializations.length > 0 && (
              <div>
                <Text size="sm" c="dimmed" mb={4}>Specializations</Text>
                <Group gap={4} wrap="wrap">
                  {selectedActor.specializations.map((spec) => (
                    <Badge key={spec} variant="outline" size="sm" color="cyan">
                      {spec}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}

            {/* License Info */}
            {selectedActor.licenseNumber && (
              <div>
                <Text size="sm" c="dimmed">License</Text>
                <Text size="sm">
                  {selectedActor.licenseNumber}
                  {selectedActor.licenseJurisdiction && ` (${selectedActor.licenseJurisdiction})`}
                </Text>
              </div>
            )}

            <Divider />

            {/* Role Assignment */}
            <div>
              <Text size="sm" fw={600} mb="xs">Role Assignment</Text>
              <MultiSelect
                data={roleOptions}
                value={editRoleIds}
                onChange={handleRoleChange}
                placeholder="Select roles..."
                searchable
                clearable
              />
            </div>

            {/* Scope Preview */}
            <div>
              <Group gap="xs" mb="xs">
                <IconShieldCheck size={16} />
                <Text size="sm" fw={600}>Scope Preview</Text>
                {scopesLoading && <Text size="xs" c="dimmed">(resolving...)</Text>}
              </Group>
              {previewScopes.length === 0 ? (
                <Text size="xs" c="dimmed">No scopes resolved for selected roles.</Text>
              ) : (
                <Group gap={4} wrap="wrap">
                  {previewScopes.map((scope) => (
                    <Badge key={scope} variant="dot" size="sm" color="blue">
                      {scope}
                    </Badge>
                  ))}
                </Group>
              )}
            </div>

            {/* Constellation Memberships (DRR-126/134) */}
            {constellationMemberships.length > 0 && (
              <div>
                <Group gap="xs" mb="xs">
                  <IconNetwork size={16} />
                  <Text size="sm" fw={600}>Constellation Memberships</Text>
                </Group>
                <Stack gap={4}>
                  {constellationMemberships.map((m) => (
                    <Group key={`${m.constellationId}-${m.edgeType}`} gap="xs">
                      <Badge variant="light" size="xs" color="indigo">
                        Ring {m.ring}
                      </Badge>
                      <Text size="xs">{m.edgeType}</Text>
                      <Text size="xs" c="dimmed">{m.role}</Text>
                    </Group>
                  ))}
                </Stack>
              </div>
            )}

            <Divider />

            {/* Save error */}
            {saveError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {saveError}
              </Alert>
            )}

            {/* Save */}
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={handleSave}
              loading={saving}
              disabled={!isDirty}
              fullWidth
            >
              Save Role Changes
            </Button>
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}
